import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'mui-rff';
import { search } from 'fast-fuzzy';
import { TextField } from 'src/shared/components/Form/TextField';
import { type HTMLAttributes } from 'react';

const defaultFieldMapping = {
  firstName: 'firstName',
  documentType: 'documentType',
  documentNumber: 'personalId',
  email: 'email',
  phone: 'phone',
  payrollCode: 'payrollCode',
  status: 'status',
  netSalary: 'netSalary',
  grossSalary: 'grossSalary',
  accountNumber: 'accountNumber'
};

type SelectOption = {
  id?: string;
  label: string;
  value: string;
};

export type DataTableRow = { fieldInPayflow: SelectOption };

const customFieldSearchTerms: Record<keyof typeof defaultFieldMapping, string[]> = {
  firstName: ['first name', 'nombre'],
  documentType: ['tipo de documento'],
  documentNumber: ['id number', 'personal id', 'document number', 'dni/nie', 'nif', 'dni'],
  email: ['email', 'correo'],
  phone: ['phone', 'móvil'],
  payrollCode: ['id', 'codigo', 'payroll id'],
  status: ['status', 'estado'],
  netSalary: ['salario payflow', 'remuneracion neta', 'net salary', 'salario neto'],
  grossSalary: ['salario payflow', 'remuneracion bruta', 'gross salary', 'salario bruto'],
  accountNumber: ['account number', 'cuenta bancaria', 'iban']
};

const fuzzyOptions = {
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  useSeparatedUnicode: true,
  returnMatchData: true,
  threshold: 0,
  keySelector: (option: SelectOption) => option.label
} as const;

const getSortedOptionsByMatch = (options: SelectOption[], searchTerms: string[]): SelectOption[] => {
  if (searchTerms.length === 0) {
    return options;
  }

  const scoreByOption = new Map<string, number>();

  searchTerms
    .map(word => word.trim())
    .filter(Boolean)
    .forEach(currentSearchWord => {
      search(currentSearchWord, options, fuzzyOptions).forEach(({ item, score }) => {
        const previousScore = scoreByOption.get(item.label) ?? 0;
        if (score > previousScore) {
          scoreByOption.set(item.label, score);
        }
      });
    });

  return [...options].sort((a, b) => (scoreByOption.get(b.label) ?? 0) - (scoreByOption.get(a.label) ?? 0));
};

export const useColumns = (payrollSoftwareName: string) => {
  const { t } = useTranslation(['common']);

  return [
    {
      accessor: 'fieldInPayflow.label',
      title: t('table.field in softwareName', { softwareName: 'Payflow' }),
      getTableCellProps: () => ({
        sx: {
          fontWeight: '600'
        }
      }),
      renderCell: (row: DataTableRow) => {
        return t(row.fieldInPayflow.label);
      }
    },
    {
      accessor: 'fieldInPayflow.value',
      title: t('table.field in softwareName', { softwareName: payrollSoftwareName }),
      renderCell: (row: DataTableRow) => {
        return (
          <TextField
            InputLabelProps={{
              shrink: true
            }}
            FormHelperTextProps={{
              sx: {
                display: 'none'
              }
            }}
            name={`customFields.${defaultFieldMapping[row.fieldInPayflow.value as keyof typeof defaultFieldMapping]}`}
            label={t('table.field in softwareName', { softwareName: payrollSoftwareName })}
          />
        );
      }
    }
  ];
};

/**
 * Selector instead of text fields to ensure that admins don't input not existing fields
 * Migration to this version will be progressive, so we need to keep the old version for now
 */
export const useColumnsV2 = (payrollSoftwareName: string, customFields?: SelectOption[]) => {
  const { t } = useTranslation(['common']);

  return [
    {
      accessor: 'fieldInPayflow.label',
      title: t('table.field in softwareName', { softwareName: 'Payflow' }),
      getTableCellProps: () => ({
        sx: {
          fontWeight: '600'
        }
      }),
      renderCell: (row: DataTableRow) => {
        return t(row.fieldInPayflow.label);
      }
    },
    {
      accessor: 'fieldInPayflow.value',
      title: t('table.field in softwareName', { softwareName: payrollSoftwareName }),
      renderCell: (row: DataTableRow) => {
        const payflowField = row.fieldInPayflow.value as keyof typeof defaultFieldMapping;
        const searchTerms = customFieldSearchTerms[payflowField];
        const sortedOptions = getSortedOptionsByMatch(customFields ?? [], searchTerms);
        const fieldName = `customFields.${defaultFieldMapping[payflowField]}`;

        return (
          <Autocomplete
            name={fieldName}
            label={
              customFields && customFields.length > 0
                ? t('table.field in softwareName', { softwareName: payrollSoftwareName })
                : t('table.noCustomFields in softwareName', { softwareName: payrollSoftwareName })
            }
            options={sortedOptions}
            renderOption={(props, option: SelectOption) => {
              return (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              );
            }}
            getOptionValue={(option: SelectOption) => option.value}
            textFieldProps={{
              InputLabelProps: {
                shrink: true
              },
              FormHelperTextProps: {
                sx: {
                  display: 'none'
                }
              }
            }}
            disabled={!customFields || customFields.length === 0}
          />
        );
      }
    }
  ];
};
